import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useData } from '../../context/DataContext';
import { formatCurrency } from '../../utils/formatters';
import {
  calculateEquity,
  calculateLTV,
  generatePropertyMortgageProjection,
  projectPropertyValue,
  getMortgagePaymentInfo,
} from '../../utils/mortgageCalculations';
import { ResponsiveContainer, ComposedChart, Line, Area, XAxis, YAxis, Tooltip, Legend, CartesianGrid } from 'recharts';
import dayjs from 'dayjs';

const PropertyDetail = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const { properties, mortgages } = useData();
  const [projectionYears, setProjectionYears] = useState(10);

  const property = properties.find(p => p.id === id);
  if (!property) {
    return (
      <div className="max-w-7xl mx-auto px-4 py-6">
        <p className="text-gray-600">Property not found.</p>
        <button onClick={() => navigate('/properties')} className="text-blue-600 hover:underline mt-2">Back to Properties</button>
      </div>
    );
  }

  const mortgage = mortgages.find(m => m.id === property.linkedMortgageId);
  const equity = calculateEquity(property.currentValue, mortgage?.currentBalance || 0);
  const ltv = mortgage ? calculateLTV(mortgage.currentBalance, property.currentValue) : 0;
  const gain = property.purchasePrice > 0 ? property.currentValue - property.purchasePrice : 0;
  const gainPercent = property.purchasePrice > 0 ? ((gain) / property.purchasePrice) * 100 : 0;

  // Generate projection data
  const projectionMonths = projectionYears * 12;
  const projectionData = generatePropertyMortgageProjection(property, mortgage, projectionMonths);

  // Property-only projections (low/avg/high growth scenarios)
  const lowGrowth = Math.max(0, property.annualGrowthRate - 2);
  const highGrowth = property.annualGrowthRate + 2;

  const propertyProjectionData = [];
  for (let m = 0; m <= projectionMonths; m += (projectionYears <= 5 ? 1 : 12)) {
    const date = dayjs().add(m, 'month');
    propertyProjectionData.push({
      displayDate: date.format(projectionYears <= 5 ? 'MMM YY' : 'YYYY'),
      low: projectPropertyValue(property.currentValue, lowGrowth, m),
      average: projectPropertyValue(property.currentValue, property.annualGrowthRate, m),
      high: projectPropertyValue(property.currentValue, highGrowth, m),
    });
  }

  // Mortgage payment info
  const paymentInfo = mortgage ? getMortgagePaymentInfo(mortgage) : null;

  // Thin out chart data for large projections
  const step = projectionYears <= 5 ? 1 : projectionYears <= 15 ? 3 : 6;
  const chartData = projectionData.filter((_, i) => i === 0 || i % step === 0 || i === projectionData.length - 1);

  const formatYAxis = (value) => {
    if (value >= 1000000) return `${(value / 1000000).toFixed(1)}M`;
    if (value >= 1000) return `${(value / 1000).toFixed(0)}k`;
    return value;
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
      <div className="mb-6">
        <button onClick={() => navigate('/properties')} className="text-sm text-gray-600 hover:text-gray-900 flex items-center gap-1">
          ← Back to Properties
        </button>
        <h1 className="text-2xl font-bold text-gray-900 mt-2">{property.name}</h1>
        {property.address && <p className="text-sm text-gray-500 mt-1">{property.address}</p>}
      </div>

      {/* Summary Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
          <p className="text-xs text-gray-500 uppercase tracking-wide">Current Value</p>
          <p className="text-xl font-bold text-gray-900 mt-1">{formatCurrency(property.currentValue, property.currency)}</p>
        </div>
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
          <p className="text-xs text-gray-500 uppercase tracking-wide">Equity</p>
          <p className="text-xl font-bold text-green-600 mt-1">{formatCurrency(equity, property.currency)}</p>
        </div>
        {mortgage && (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
            <p className="text-xs text-gray-500 uppercase tracking-wide">LTV</p>
            <p className="text-xl font-bold text-gray-900 mt-1">{ltv.toFixed(1)}%</p>
          </div>
        )}
        {property.purchasePrice > 0 && (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
            <p className="text-xs text-gray-500 uppercase tracking-wide">Gain Since Purchase</p>
            <p className={`text-xl font-bold mt-1 ${gain >= 0 ? 'text-green-600' : 'text-red-600'}`}>
              {gain >= 0 ? '+' : ''}{formatCurrency(gain, property.currency)}
            </p>
            <p className={`text-xs ${gain >= 0 ? 'text-green-500' : 'text-red-500'}`}>
              {gainPercent >= 0 ? '+' : ''}{gainPercent.toFixed(1)}%
            </p>
          </div>
        )}
      </div>

      {/* Linked Mortgage Summary */}
      {mortgage && paymentInfo && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-6">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-semibold text-gray-900">Linked Mortgage: {mortgage.name}</h3>
            <button
              onClick={() => navigate(`/mortgages/${mortgage.id}`)}
              className="text-xs text-blue-600 hover:underline"
            >
              View Details →
            </button>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div>
              <p className="text-xs text-gray-500">Balance</p>
              <p className="text-sm font-bold text-gray-900">{formatCurrency(mortgage.currentBalance, mortgage.currency)}</p>
            </div>
            <div>
              <p className="text-xs text-gray-500">Monthly Payment</p>
              <p className="text-sm font-bold text-gray-900">{formatCurrency(paymentInfo.monthlyPayment, mortgage.currency)}</p>
            </div>
            <div>
              <p className="text-xs text-gray-500">Current Rate</p>
              <p className="text-sm font-bold text-gray-900">
                {paymentInfo.currentRate}%
                <span className="text-xs font-normal text-gray-500 ml-1">
                  ({paymentInfo.isInFixedPeriod ? 'Fixed' : 'SVR'})
                </span>
              </p>
            </div>
            <div>
              <p className="text-xs text-gray-500">Remaining Term</p>
              <p className="text-sm font-bold text-gray-900">
                {Math.floor(paymentInfo.remainingMonths / 12)}y {paymentInfo.remainingMonths % 12}m
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Property Value vs Mortgage Balance Chart */}
      {mortgage && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-sm font-semibold text-gray-900">Property Value vs Mortgage Balance</h3>
            <div className="flex gap-2">
              {[5, 10, 15, 25].map(years => (
                <button
                  key={years}
                  onClick={() => setProjectionYears(years)}
                  className={`px-3 py-1 text-xs rounded-lg ${projectionYears === years ? 'text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
                  style={projectionYears === years ? { backgroundColor: '#012E58' } : {}}
                >
                  {years}y
                </button>
              ))}
            </div>
          </div>
          <ResponsiveContainer width="100%" height={350}>
            <ComposedChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis dataKey="displayDate" tick={{ fontSize: 11 }} interval="preserveStartEnd" />
              <YAxis tickFormatter={formatYAxis} tick={{ fontSize: 11 }} />
              <Tooltip
                formatter={(value, name) => [formatCurrency(value, property.currency), name]}
                labelStyle={{ fontWeight: 'bold' }}
              />
              <Legend />
              <Area type="monotone" dataKey="equity" fill="#bfdbfe" stroke="none" name="Equity" />
              <Line type="monotone" dataKey="propertyValue" stroke="#059669" strokeWidth={2} dot={false} name="Property Value" />
              <Line type="monotone" dataKey="mortgageBalance" stroke="#dc2626" strokeWidth={2} dot={false} name="Mortgage Balance" />
            </ComposedChart>
          </ResponsiveContainer>
        </div>
      )}

      {/* Property Value Projection (Low/Avg/High) */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-sm font-semibold text-gray-900">Property Value Projection</h3>
          <div className="flex items-center gap-2">
            <span className="text-xs text-gray-500">
              Growth: {lowGrowth}% / {property.annualGrowthRate}% / {highGrowth}%
            </span>
          </div>
        </div>
        <ResponsiveContainer width="100%" height={300}>
          <ComposedChart data={propertyProjectionData}>
            <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
            <XAxis dataKey="displayDate" tick={{ fontSize: 11 }} interval="preserveStartEnd" />
            <YAxis tickFormatter={formatYAxis} tick={{ fontSize: 11 }} />
            <Tooltip formatter={(value, name) => [formatCurrency(value, property.currency), name]} />
            <Legend />
            <Line type="monotone" dataKey="high" stroke="#059669" strokeWidth={1.5} strokeDasharray="5 5" dot={false} name={`High (${highGrowth}%)`} />
            <Line type="monotone" dataKey="average" stroke="#7c3aed" strokeWidth={2} dot={false} name={`Average (${property.annualGrowthRate}%)`} />
            <Line type="monotone" dataKey="low" stroke="#f59e0b" strokeWidth={1.5} strokeDasharray="5 5" dot={false} name={`Low (${lowGrowth}%)`} />
          </ComposedChart>
        </ResponsiveContainer>

        {/* Projected values table */}
        <div className="mt-4 overflow-x-auto">
          <table className="min-w-full text-xs">
            <thead>
              <tr className="border-b border-gray-200">
                <th className="text-left py-2 pr-4 text-gray-500 font-medium">Timeframe</th>
                <th className="text-right py-2 px-4 text-gray-500 font-medium">Low ({lowGrowth}%)</th>
                <th className="text-right py-2 px-4 text-gray-500 font-medium">Average ({property.annualGrowthRate}%)</th>
                <th className="text-right py-2 px-4 text-gray-500 font-medium">High ({highGrowth}%)</th>
              </tr>
            </thead>
            <tbody>
              {[5, 10, 15, 20, 25].map(years => (
                <tr key={years} className="border-b border-gray-100">
                  <td className="py-2 pr-4 text-gray-700">{years} years</td>
                  <td className="py-2 px-4 text-right text-gray-900">{formatCurrency(projectPropertyValue(property.currentValue, lowGrowth, years * 12), property.currency)}</td>
                  <td className="py-2 px-4 text-right font-medium text-gray-900">{formatCurrency(projectPropertyValue(property.currentValue, property.annualGrowthRate, years * 12), property.currency)}</td>
                  <td className="py-2 px-4 text-right text-gray-900">{formatCurrency(projectPropertyValue(property.currentValue, highGrowth, years * 12), property.currency)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default PropertyDetail;
